import React from 'react';
import KaAlert from '../Common/KaAlert'
import SpinnerButton from '../Common/SpinnerButton';
import { useDisplayMessage } from '../Common/useDisplayMessage';
import KaModal from '../Common/KaModal';
import { Path, useFieldArray, useForm } from 'react-hook-form';
import { FormTextControl, RowFormText } from '../Common/Forms/FormText';
import { SupplierDto } from '../Requests/Suppliers/useGetSuppliers';
import useUpsertSupplier, { SupplierUpsertDto } from '../Requests/Suppliers/useUpsertSupplier';
import { Col, FormGroup } from 'reactstrap';
import BoldButton from '../Common/BoldButton';
import { FormLabel } from '../Common/Forms/FormLabel';

interface IProps {
  initSupplier?: SupplierDto,
  onHide: () => void,
  onSuccess: (supplier: SupplierUpsertDto) => void
}

type SupplierForm = Omit<SupplierUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

export const CreateEditSupplier = (props: IProps) => {
  const displayMessage = useDisplayMessage();
  let { id, ...initSupplier } = { ...props.initSupplier };
  const initEmails = initSupplier.emailAddresses?.map(email => ({ email })) ?? [];

  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<SupplierForm>({
    defaultValues: {
      ...initSupplier,
      emailAddresses: initEmails.length === 0 ? [{ email: '' }] : initEmails,
    }
  });

  const emailAddresses = useFieldArray({ control, name: 'emailAddresses' });

  const validateUniqueEmail = (value: string) => {
    if (value && getValues('emailAddresses').map(f => f.email).filter(e => e === value).length > 1) {
      return 'Email already exists';
    }
    return true;
  };

  const removeEmail = (index: number) => {
    emailAddresses.remove(index);
    trigger('emailAddresses');
  }

  const handleCreateUpdate = (formValue: SupplierForm) => {
    useCreateEdit.request({
      ...formValue,
      emailAddresses: formValue.emailAddresses.map(e => e.email).filter(e => !!e),
    });
  }

  const useCreateEdit = useUpsertSupplier(props.initSupplier?.id, {
    onSuccess: () => {
      const formValues = getValues();
      props.onSuccess({
        ...formValues,
        emailAddresses: formValues.emailAddresses.map(e => e.email).filter(e => !!e),
      });
    },
    onError: (e) => displayMessage.fail(e.message),
  });

  const hookProps = (name: Path<SupplierForm>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  }

  const labelSpace = 4;
  const inputSpace = 7;

  const emailControl = (index: number) =>
    <FormTextControl
      {...hookProps(`emailAddresses.${index}.email`)}
      displayName='Email address'
      options={{
        onChange: () => trigger('emailAddresses'),
        validate: validateUniqueEmail,
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Email Address is invalid'
        }
      }} />

  return (<KaModal
    onHide={onClose}
    show={true}
    title={`${!props.initSupplier ? 'Create' : 'Update'} a supplier`}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText
        {...hookProps("name")}
        options={{
          required: 'Name is required',
          validate: {
            length: v => v.length < 51 || 'Name cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("accountNumber")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Account Number cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressStreet")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressCity")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Address City cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressState")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Address State cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressPostalCode")}
        options={{
          validate: {
            length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressCountry")}
        options={{
          validate: {
            length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters'
          }
        }}
      />
      {
        emailAddresses.fields.map((field, index) => {
          const isFirst = index === 0;
          const showX = emailAddresses.fields.length > 1;
          return <FormGroup row key={field.id}>
            {isFirst &&
              <Col xs={{ size: labelSpace }}>
                <FormLabel displayName='Email' {...hookProps(`emailAddresses.${index}.email`)} />
              </Col>
            }
            <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
              {emailControl(index)}
            </Col>
            {
              showX &&
              <Col xs="1" className="pe-0">
                <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
              </Col>
            }
          </FormGroup>
        })}
      <FormGroup row>
        <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
          <BoldButton
            className='col-auto btn-ghost-primary'
            disabled={emailAddresses.fields.length >= 10}
            onClick={() => emailAddresses.append({ email: '' })}>
            Add Email
          </BoldButton>
        </Col>
      </FormGroup>
    </>)}
    footer={(
      <SpinnerButton
        className="btn-ghost-primary"
        spinning={useCreateEdit.isLoading}
        onClick={handleSubmit(handleCreateUpdate)}>
        {`${!props.initSupplier ? 'Create' : 'Update'}`}
      </SpinnerButton>)
    }
  />);
}
